import { clamp } from "lodash-es";

import type {
  AnyCoordinate,
  Line,
  Rectangle,
  RelativeCoordinate,
  SizedRectangle,
  ViewportCoordinate,
  WindowCoordinate,
  WindowLen,
} from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import { isNonPrimitive } from "@/utils/type";

export function isCoordinate<T extends AnyCoordinate>(c?: unknown): c is T {
  return isNonPrimitive(c) && "x" in c;
}

export function rectangle<T extends AnyCoordinate>(
  value: { from: T; to: T } | { center: T; size: T } | { from: T; size: T },
): Rectangle<T> {
  if ("from" in value && "to" in value) {
    return { p0: value.from, p1: value.to };
  }

  if ("center" in value && "size" in value) {
    const halfSize = divided(value.size, 2);
    return { p0: minus(value.center, halfSize), p1: plus(value.center, halfSize) };
  }

  return { p0: value.from, p1: plus(value.from, value.size) };
}

export function sizedRectangle<T extends AnyCoordinate>(value: {
  from: T;
  size: T;
}): SizedRectangle<T> {
  return { p0: value.from, size: value.size };
}

export function plus(a: ViewportCoordinate, b: WindowLen): ViewportCoordinate;
export function plus(a: WindowCoordinate, b: WindowLen): WindowCoordinate;
export function plus<T extends AnyCoordinate>(a: T, b?: T): T;
export function plus<T extends AnyCoordinate>(a: T, b: T): T {
  return { x: a.x + b.x, y: a.y + b.y } as T;
}

export function minus(a: ViewportCoordinate, b: WindowLen): ViewportCoordinate;
export function minus(a: WindowCoordinate, b: WindowLen): WindowCoordinate;
export function minus<T extends AnyCoordinate>(a: T, b?: T): T;
export function minus<T extends AnyCoordinate>(a: T, b?: T): T {
  return (b ? { x: a.x - b.x, y: a.y - b.y } : { x: -a.x, y: -a.y }) as T;
}

export function times<T extends AnyCoordinate>(a: T, factor1: number, factor2?: number): T;
export function times<T extends AnyCoordinate>(a: T, factor: T): T;
export function times<T extends AnyCoordinate>(a: T, factor1: number | T, factor2?: number): T {
  return isCoordinate<T>(factor1)
    ? ({ x: a.x * factor1.x, y: a.y * factor1.y } as T)
    : ({ x: a.x * factor1, y: a.y * (factor2 ?? (factor1 as number)) } as T);
}

export function divided<T extends AnyCoordinate>(a: T, factor1: number, factor2?: number): T;
export function divided<T extends AnyCoordinate>(a: T, factor1: T): T;
export function divided<T extends AnyCoordinate>(a: T, factor1: number | T, factor2?: number): T {
  return isCoordinate<T>(factor1)
    ? ({ x: a.x / factor1.x, y: a.y / factor1.y } as T)
    : ({ x: a.x / factor1, y: a.y / (factor2 ?? (factor1 as number)) } as T);
}

export function project<T extends AnyCoordinate>(a: T, angle: number): T {
  return times(a, Math.cos(angle), Math.sin(angle));
}

export function sumCoord<T extends AnyCoordinate>(as: T[]): T {
  let x = 0,
    y = 0;
  for (const a of as) {
    x += a.x;
    y += a.y;
  }
  return { x, y } as T;
}

export function maxCoord<T extends AnyCoordinate>(
  as: T[],
  initial = { x: -Infinity, y: -Infinity } as T,
): T {
  return as.reduce(
    (res, a) => ({ x: Math.max(res.x, a.x), y: Math.max(res.y, a.y) }) as T,
    initial,
  );
}

export function minCoord<T extends AnyCoordinate>(
  as: T[],
  initial = { x: Infinity, y: Infinity } as T,
): T {
  return as.reduce(
    (res, a) => ({ x: Math.min(res.x, a.x), y: Math.min(res.y, a.y) }) as T,
    initial,
  );
}

export function clampRelativeCoord(c: RelativeCoordinate): RelativeCoordinate {
  return relativeCoord(clamp(c.x, 0, 1), clamp(c.y, 0, 1));
}

export function clampCoord<T extends AnyCoordinate>(c: T, min: T, max: T): T {
  return { x: clamp(c.x, min.x, max.x), y: clamp(c.y, min.y, max.y) } as T;
}

export function applyCoord<T extends AnyCoordinate>(a: T, func: (n: number) => number): T {
  return { x: func(a.x), y: func(a.y) } as T;
}

export function toSize<T extends AnyCoordinate>(a: T) {
  return { width: a.x, height: a.y };
}

const ETA = 0.000001;

export function isEqual<T extends AnyCoordinate>(a: T, b: T, eta: number): boolean;
export function isEqual(a: RelativeCoordinate, b: RelativeCoordinate): boolean;
export function isEqual<T extends AnyCoordinate>(a: T, b: T, eta = ETA): boolean {
  return isNear(a.x, b.x, eta) && isNear(a.y, b.y, eta);
}

function isNear(a: number, b: number, eta: number) {
  return Math.abs(a - b) < eta;
}

export function distance2<T extends AnyCoordinate>(a: T, b: T): number {
  return (a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y);
}

export function interpolate<T extends AnyCoordinate>(v: number, a: T, b: T): T {
  return { x: a.x - v * (a.x - b.x), y: a.y - v * (a.y - b.y) } as T;
}

export function insideRectangle<T extends AnyCoordinate>(c: T, r: Rectangle<T>) {
  return c.x >= r.p0.x && c.x <= r.p1.x && c.y >= r.p0.y && c.y <= r.p1.y;
}

export function rectangleSize<T extends AnyCoordinate>(r: Rectangle<T>) {
  return minus(r.p1, r.p0);
}

/**
 * Return the point on the line that is nearest the given point.
 * @return {
 * t: the fractional position along the line 0..1 (0: p0, 1: p1, 0.5: middle of the line),
 * point: the point itself
 * }
 */
export function nearestLinePoint<T extends AnyCoordinate>(point: T, line: Line<T>) {
  const len = distance2(line.p0, line.p1);
  const t =
    ((point.x - line.p0.x) * (line.p1.x - line.p0.x) +
      (point.y - line.p0.y) * (line.p1.y - line.p0.y)) /
    len;
  return { t, point: interpolate(clamp(t, 0, 1), line.p0, line.p1) };
}

export function isHorizontal<T extends AnyCoordinate>(line: Line<T>) {
  return Math.abs(line.p0.y - line.p1.y) < ETA;
}

export function zeroX<T extends AnyCoordinate>(a: T): T {
  return { x: 0, y: a.y } as T;
}
